import { languageHelper } from "../../../config/helperFunctions";
import React, { useCallback, useEffect, useState } from "react";
import Select from "react-select";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { apiKey } from "../../../config/config";
import MessageCard from "../../../components/AdminCards/MessageCard";
import QuickAddSupplierModal from "../../../components/Modal/QuickAddSupplierModal";
import { toast } from "react-toastify";

const generateReferenceNumber = () => {
  // Generate a random number between 100000000 and 999999999
  return Math.floor(100000000 + Math.random() * 900000000).toString();
};

function AddExpensesForm() {
  const user = JSON.parse(localStorage.getItem("rms-admin"));
  const { id } = useParams();
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split("T")[0], // Format: YYYY-MM-DD
    amount: "",
    vat_percentage: "",
    tax_method: "",
    total_amount: 0,
    vat: 0,
    reference: generateReferenceNumber(),
    invoice_reference: "",
    invoice_date: "",
    paid: "",
    category_id: "",
    payment_method_id: "",
    note: "",
    employee_id: "",
    user_id: user.user.id,
    outlet_id: user.user.outlet_id,
  });
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [pageLoading, setPageLoading] = useState(false);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [showSupplierModal, setShowSupplierModal] = useState(false);

  const navigate = useNavigate();

  const handleChange = (e) =>
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!id) {
      axios
        .post(`${apiKey}/api/rms/admin/expense`, formData, {
          headers: {
            Authorization: `Bearer ${user?.token}`,
          },
        })
        .then((res) => {
          toast.success("Success", {
            position: "bottom-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "colored",
          });
          navigate(-1);
        })
        .catch((err) => {
          Object.keys(err.response.data.data).forEach((item) => {
            err.response.data.data[item].forEach((i) => {
              toast.error(i || "Validation Error");
            });
          });
        })
        .finally(() => setLoading(false));
    } else {
      axios
        .put(`${apiKey}/api/rms/admin/expense/${id}`, formData, {
          headers: {
            Authorization: `Bearer ${user?.token}`,
          },
        })
        .then((res) => {
          toast.success("Success", {
            position: "bottom-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "colored",
          });
          navigate(-1);
        })
        .catch((err) => {
          Object.keys(err.response.data.data).forEach((item) => {
            err.response.data.data[item].forEach((i) => {
              toast.error(i || "Validation Error");
            });
          });
        })
        .finally(() => setLoading(false));
    }
  };

  const fetchExpenseCat = useCallback(() => {
    axios
      .get(`${apiKey}/api/rms/admin/expensescategory`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
        params: {
          outletId: user?.user.outlet_id,
        },
      })
      .then((res) => {
        setCategories(
          res.data.map((cat, index) => ({
            label: cat.name,
            vat_percentage: cat.vat_percentage,
            value: cat.id,
            tax_method: cat.tax_method,
            index,
          }))
        );
      })
      .catch((err) => {
        setError("Cannot load data");
      })
      .finally(() => {
        // setPageLoading(false);
      });
  }, [user?.token, user?.user.outlet_id]);

  const fetchUsers = useCallback(() => {
    axios
      .get(`${apiKey}/api/rms/admin/user`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
        params: {
          outletId: user?.user.outlet_id,
        },
      })
      .then((res) => {
        setUsers(
          res.data.map((user) => ({
            label: user.name,
            value: user.id,
          }))
        );
      })
      .catch((err) => {
        setError("Cannot load data");
      })
      .finally(() => {
        // setPageLoading(false);
      });
  }, [user?.token, user?.user.outlet_id]);

  const fetchPayment = useCallback(() => {
    axios
      .get(`${apiKey}/api/rms/admin/get-all-payment`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      })
      .then((res) => {
        setPaymentMethods(
          res.data.map((method, index) => {
            // if (method.id >= 5) return;
            return {
              label: method.name,
              value: method.id,
              index,
            };
          })
        );
      })
      .catch((err) => {
        setError("Cannot load data");
      })
      .finally(() => {
        // setPageLoading(false);
      });
  }, [user?.token]);

  const fetchSuppliers = useCallback(() => {
    axios
      .get(`${apiKey}/api/rms/admin/suppliers`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
        params: {
          outletId: user?.user.outlet_id,
        },
      })
      .then((res) => {
        const mappedSuppliers = res.data.map((sup) => ({
          label: sup.name,
          value: sup.id,
        }));
        setSuppliers(mappedSuppliers);
        setError(false);
      })
      .catch((err) => {
        setError(err.response?.data.message || "Error Occured");
      })
      .finally(() => { });
  }, [user?.token, user?.user.outlet_id]);

  useEffect(() => {
    fetchExpenseCat();
    fetchUsers();
    fetchPayment();
    fetchSuppliers();
  }, [fetchExpenseCat, fetchPayment, fetchUsers, fetchSuppliers]);

  useEffect(() => {
    // cat_index is only set when the user actively picks a category from the
    // dropdown (see onChange below). It's never set when an existing expense
    // is loaded for editing -- without this guard, this effect fired as soon
    // as the categories list loaded and stomped the just-fetched real
    // vat_percentage/tax_method with 0, breaking edit mode.
    if (typeof formData.cat_index !== "number" || !categories[formData.cat_index]) {
      return;
    }
    setFormData((prev) => ({
      ...prev,
      vat_percentage: categories[formData.cat_index]?.vat_percentage || 0,
      tax_method: categories[formData.cat_index]?.tax_method || 0,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, formData.cat_index]);

  useEffect(() => {
    if (formData.amount > 0 && formData.vat_percentage > 0) {
      let vat = 0;
      let baseAmount = 0;
      let totalAmount = 0;

      if (formData.tax_method.toLowerCase() === "inclusive") {
        // VAT Inclusive
        baseAmount =
          parseFloat(formData.amount) / (1 + formData.vat_percentage / 100); // Value before VAT
        vat = parseFloat(formData.amount) - baseAmount; // VAT Value
        totalAmount = parseFloat(formData.amount); // Already inclusive of VAT
      } else if (formData.tax_method.toLowerCase() === "exclusive") {
        // VAT Exclusive
        vat = parseFloat(formData.amount) * (formData.vat_percentage / 100); // VAT Value
        baseAmount = parseFloat(formData.amount); // Value before VAT
        totalAmount = parseFloat(formData.amount) + vat; // Total with VAT
      } else {
        // No VAT
        baseAmount = parseFloat(formData.amount);
        vat = 0;
        totalAmount = parseFloat(formData.amount);
      }

      // Update state with calculated values
      setFormData((prevData) => ({
        ...prevData,
        base_amount: baseAmount.toFixed(2), // Value before VAT
        vat: vat.toFixed(2), // VAT Value
        total_amount: totalAmount.toFixed(2), // Total Value
      }));
    }
  }, [formData.amount, formData.vat_percentage, formData.tax_method]);

  useEffect(() => {
    if (id) {
      setPageLoading(true);
      const fetchExpenseCat = () => {
        axios
          .get(`${apiKey}/api/rms/admin/expense/${id}`, {
            headers: {
              Authorization: `Bearer ${user?.token}`,
            },
          })
          .then((res) => {
            setFormData(res.data);
          })
          .catch((err) => {
            setError(err.response.data.message);
          })
          .finally(() => setPageLoading(false));
      };
      fetchExpenseCat();
    }
  }, [id, user?.token]);

  if (pageLoading) {
    return (
      <div className="absolute top-0 left-0 w-full h-full grid place-items-center">
        <img src="/assets/gif/dash2.gif" alt="" />
      </div>
    );
  }

  if (error) {
    return <MessageCard error={error} />;
  }

  return (
    <main>
      <div className="relative my-16 flex flex-col min-w-0 break-words w-full mb-6 shadow-lg bg-white rounded-lg p-4 border-0">
        <div className="flex justify-between items-center mb-4">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-d-bg-text text-2xl"> {languageHelper("Add Expenses")}</h1>
          </div>
        </div>
        <div className="w-full">
          <form action="" className="w-full" onSubmit={handleSubmit}>
            <div className="flex flex-col md:flex-row gap-x-8 gap-y-2 w-full ">
              <label htmlFor="" className="w-full md:w-1/3">
                <span className="text-d-bg-text text-sm mb-2 relative before:content-['*'] before:text-red-500 before:absolute before:top-0 before:right-0 before:translate-x-2 font-bold">
                  {languageHelper("Date")}
                </span>
                <div className="mb-2 pt-0">
                  <input
                    type="date"
                    placeholder="2024-01-19"
                    className="px-2 py-2 placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full"
                    required
                    value={formData.date}
                    onChange={handleChange}
                  // disabled
                  />
                </div>
              </label>

              <label htmlFor="" className="w-full md:w-1/3">
                <span className="text-d-bg-text text-sm mb-2 font-bold">
                  {languageHelper("Responsible Person")}
                </span>
                <div className="mb-2 pt-0">
                  <Select
                    type="text"
                    placeholder={languageHelper("Select")}
                    className="placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full "
                    required
                    options={users}
                    defaultValue={{
                      value: formData.employee?.id,
                      label: formData.employee?.name,
                    }}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        employee_id: e.value,
                      })
                    }
                  />
                </div>
              </label>

              <label htmlFor="" className="w-full md:w-1/3">
                <span className="text-d-bg-text text-sm mb-2 font-bold">
                  {languageHelper("Reference No")}
                </span>
                <div className="mb-2 pt-0">
                  <input
                    type="text"
                    placeholder="429945702"
                    className=" px-2 py-2 placeholder-blueGray-300 text-blueGray-600 relative bg-gray-100 rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full "
                    required
                    value={formData.reference}
                    onChange={handleChange}
                    disabled
                  />
                </div>
              </label>
            </div>

            <div className="flex flex-col md:flex-row gap-x-8 gap-y-2 w-full ">
              <label htmlFor="" className="w-full md:w-1/3">
                <span className="text-d-bg-text text-sm mb-2 relative before:content-['*'] before:text-red-500 before:absolute before:top-0 before:right-0 before:translate-x-2  font-bold">
                  {languageHelper("Category")}
                </span>
                <div className="mb-2 pt-0">
                  <Select
                    type="text"
                    placeholder={languageHelper("Select")}
                    className="placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full"
                    required
                    options={categories}
                    defaultValue={{
                      value: formData.category?.id,
                      label: formData.category?.name,
                    }}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        category_id: e.value,
                        cat_index: e.index,
                      })
                    }
                  />
                </div>
              </label>

              <label htmlFor="" className="w-full md:w-1/3">
                <span className="text-d-bg-text text-sm mb-2  font-bold">
                  {languageHelper("Vat Percentage")}
                </span>
                <div className="mb-2 pt-0">
                  <input
                    type="text"
                    placeholder={languageHelper("Vat Percentage")}
                    className=" px-2 py-2 placeholder-blueGray-300 text-blueGray-600 relative bg-gray-100 rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full "
                    required
                    value={formData.vat_percentage}
                    onChange={handleChange}
                    disabled
                  />
                </div>
              </label>

              <label htmlFor="" className="w-full md:w-1/3">
                <span className="text-d-bg-text text-sm mb-2 font-bold">
                  {languageHelper("Tax method")}
                </span>
                <div className="mb-2 pt-0">
                  <input
                    type="text"
                    placeholder={languageHelper("Tax method")}
                    className=" px-2 py-2 placeholder-blueGray-300 text-blueGray-600 relative bg-gray-100 rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full "
                    name="tax_method"
                    required
                    value={formData.tax_method}
                    onChange={handleChange}
                    disabled
                  />
                </div>
              </label>
            </div>

            <div className="flex flex-col md:flex-row gap-x-8 gap-y-2 w-full ">
              <label htmlFor="" className="w-full md:w-1/3">
                <span className="text-d-bg-text text-sm mb-2 relative before:content-['*'] before:text-red-500 before:absolute before:top-0 before:right-0 before:translate-x-2   font-bold">
                  {languageHelper("Amount")}
                </span>
                <div className="mb-2 pt-0 ">
                  <input
                    type="text"
                    placeholder={languageHelper("Amount")}
                    className="px-2 py-2 placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full"
                    required
                    name="amount"
                    value={formData.amount}
                    onChange={handleChange}
                  />
                </div>
              </label>

              <label htmlFor="" className="w-full md:w-1/3 ">
                <span className="text-d-bg-text text-sm mb-2  font-bold">
                  {languageHelper("Paymeny method")}
                </span>
                <div className="mb-2 pt-0 pr-1">
                  <Select
                    type="text"
                    placeholder={languageHelper("Select")}
                    className="placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full "
                    required
                    options={paymentMethods}
                    defaultValue={{
                      value: formData.payment_method?.id,
                      label: formData.payment_method?.name,
                    }}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        payment_method_id: e.value,
                      })
                    }
                  />
                </div>
              </label>

              <label htmlFor="" className="w-full md:w-1/3 ">
                <span className="text-d-bg-text text-sm mb-2  font-bold">
                  {languageHelper("Note")}
                </span>
                <div className="mb-2 pt-0 pr-1">
                  <textarea
                    rows="3"
                    type="text"
                    placeholder={languageHelper("Note")}
                    name="note"
                    className=" px-2 py-2 placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full "
                    onChange={handleChange}
                    value={formData.note}
                  />
                </div>
              </label>
            </div>

            <div className="flex flex-col md:flex-row gap-x-8 gap-y-2 w-full ">
              <label htmlFor="" className="w-full md:w-1/3">
                <span className="text-d-bg-text text-sm mb-2 font-bold">
                  {languageHelper("Total amount with vat")}
                </span>
                <div className="mb-2 pt-0 ">
                  <input
                    type="text"
                    placeholder={languageHelper("Amount")}
                    className="px-2 py-2 placeholder-blueGray-300 text-blueGray-600 relative bg-gray-100 rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full"
                    value={formData.total_amount}
                    onChange={handleChange}
                    disabled
                  />
                </div>
              </label>

              <label htmlFor="" className="w-full md:w-1/3 ">
                <span className="text-d-bg-text text-sm mb-2  font-bold">
                  {languageHelper("Vat")}
                </span>
                <div className="mb-2 pt-0 pr-1">
                  <input
                    type="text"
                    placeholder={languageHelper("Vat")}
                    className=" px-2 py-2 placeholder-blueGray-300 text-blueGray-600 relative bg-gray-100 rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full "
                    required
                    value={formData.vat}
                    onChange={handleChange}
                    disabled
                  />
                </div>
              </label>

              <label htmlFor="" className="w-full md:w-1/3">
                <span className="text-d-bg-text text-sm mb-2 font-bold">
                  {languageHelper("Invoice Number")}
                </span>
                <div className="mb-2 pt-0">
                  <input
                    type="text"
                    placeholder={languageHelper("Invoice Number")}
                    className="px-2 py-2 placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full"
                    name="invoice_reference"
                    value={formData.invoice_reference}
                    onChange={handleChange}
                  />
                </div>
              </label>

              <label htmlFor="" className="w-full md:w-1/3">
                <span className="text-d-bg-text text-sm mb-2 font-bold">
                  {languageHelper("Invoice Date")}
                </span>
                <div className="mb-2 pt-0">
                  <input
                    type="date"
                    className="px-2 py-2 placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full"
                    name="invoice_date"
                    value={formData.invoice_date || ""}
                    onChange={handleChange}
                  />
                </div>
              </label>
            </div>

            <div className="flex flex-col md:flex-row gap-x-8 gap-y-2 w-full ">
              <label htmlFor="" className="w-full md:w-1/3 ">
                <span className="text-d-bg-text text-sm mb-2 font-bold">{languageHelper("Supplier")}</span>
                <div className="flex items-center">
                  <Select
                    className="w-full
                    text-sm placeholder-gray-400 text-gray-700 bg-white rounded-md border border-gray-300 focus:outline-none
                    focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    classNamePrefix="select"
                    options={suppliers}
                    value={
                      suppliers.find((s) => s.value === formData.supplier_id) ||
                      null
                    }
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        supplier_id: e.value,
                      });
                    }}
                    required
                  />
                  <span
                    className="text-3xl mx-1 text-d-bg-text cursor-pointer font-bold"
                    onClick={() => setShowSupplierModal(true)}
                    title="Quick add supplier"
                  >
                    +
                  </span>
                </div>
              </label>
            </div>

            {formData.supplier_id && (
              <div className="flex flex-col md:flex-row gap-x-8 gap-y-2 w-full ">
                <label htmlFor="" className="w-full md:w-1/3 ">
                  <span className="text-d-bg-text text-sm mb-2 font-bold">{languageHelper("Paid")}</span>
                  <div className="mb-2 pt-0">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      name="paid"
                      className="px-2 py-2 placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full"
                      placeholder={languageHelper("Paid")}
                      value={formData.paid}
                      onChange={handleChange}
                    />
                  </div>
                </label>
                <label htmlFor="" className="w-full md:w-1/3 ">
                  <span className="text-d-bg-text text-sm mb-2 font-bold">{languageHelper("Due")}</span>
                  <div className="mb-2 pt-0">
                    <input
                      type="text"
                      disabled
                      className="px-2 py-2 placeholder-blueGray-300 text-blueGray-600 relative bg-gray-100 rounded text-sm shadow outline-none w-full"
                      value={(
                        (parseFloat(formData.total_amount) || 0) -
                        (parseFloat(formData.paid) || 0)
                      ).toFixed(2)}
                    />
                  </div>
                </label>
              </div>
            )}

            <span className="w-full bg-d-bg h-1 my-2 inline-block"></span>
            <div className="flex gap-2">
              <button
                className="bg-d-cat-btn text-white text-sm px-3 py-1 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 text-center w-fit relative"
                type="submit"
              >
                {loading && (
                  <span className="absolute top-0 left-0 w-full h-full py-1 px-3 bg-black bg-opacity-50">
                    <img
                      className="w-4 mx-auto"
                      src="/assets/gif/button_loader.gif"
                      alt=""
                    />
                  </span>
                )}
                {languageHelper("Submit")}
              </button>
              <button
                className="bg-d-cat-btn text-white text-sm px-3 py-1 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 text-center w-fit"
                type="button"
                onClick={() => navigate(-1)}
              >
                {languageHelper("Back")}
              </button>
            </div>
          </form>
        </div>

        {showSupplierModal && (
          <QuickAddSupplierModal
            setShowModal={setShowSupplierModal}
            setSuppliers={setSuppliers}
          />
        )}
      </div>
    </main>
  );
}

export default AddExpensesForm;
